import { useTranslation } from "react-i18next";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingCart } from "lucide-react";
import { CartItemList } from "@/components/orders/CartItemList";
import { Product, ProductSelection } from "@/types/product";
import { useProductSummary } from "@/hooks/use-product-summary";
import { useCartCountLabel } from "@/components/orders/use-cart-count-label";
import { formatCentsAsDollars } from "@/lib/price-utils";

interface OrderSummaryPanelProps {
  selectedProducts: ProductSelection[];
  showPricing?: boolean;
  products?: Product[];
  productsLoading?: boolean;
}

export function OrderSummaryPanel({
  selectedProducts,
  showPricing = false,
  products,
  productsLoading,
}: OrderSummaryPanelProps) {
  const { t } = useTranslation();
  const summary = useProductSummary({
    selectedProducts: selectedProducts.map((p) => ({
      quantity: p.quantity,
      unitPrice: "unitPrice" in p ? p.unitPrice : undefined,
    })),
    showPricing,
  });
  const countLabel = useCartCountLabel(
    selectedProducts.length,
    summary.totalItems,
  );

  return (
    <Card className="flex flex-col max-h-[50vh]">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCart className="h-4 w-4" aria-hidden="true" />
            {t("components.orders.orderSummaryPanel.orderSummary")}
            <span className="text-sm font-normal text-muted-foreground">
              ({countLabel})
            </span>
          </CardTitle>
          {showPricing && summary.totalValueCents > 0 && (
            <span className="text-sm font-semibold">
              ${formatCentsAsDollars(summary.totalValueCents)}
              {summary.hasMissingPrices ? "+" : ""}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-y-auto">
        <CartItemList
          selectedProducts={selectedProducts}
          showPricing={showPricing}
          readOnly
          products={products}
          productsLoading={productsLoading}
        />
      </CardContent>
    </Card>
  );
}
